import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { decodeJwtClaims } from "@/lib/jwt";

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "https://api.sidekick.cool";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

function isKnownExpired(token: string): boolean {
  const exp = decodeJwtClaims<{ exp?: number }>(token)?.exp;
  return typeof exp === "number" && exp * 1000 <= Date.now();
}

/**
 * End the whole session, staff included, and send the user back to /auth.
 * Exported for 401s the generic handler can't classify: a rejected staff token
 * means there is no session left to fall back to.
 */
export function expireSession() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user_data');
  localStorage.removeItem('staff_access_token');
  localStorage.removeItem('staff_user_data');
  queryClient.removeQueries({ queryKey: ["staffPermissions"] });
  queryClient.setQueryData(["user"], null);
  queryClient.setQueryData(["staffUser"], null);
  toast({
    title: "Session expired",
    description: "Please log in again.",
    variant: "destructive",
  });
}

function handleUnauthorized() {
  // A 401 always ends the *impersonated* session, but not necessarily the staff
  // one behind it. Blindly deleting access_token strands a staff user acting as
  // a sidekick: staff_access_token stays valid, yet /select-sidekick's own list
  // query reads access_token, so the picker 401s forever with no way back short
  // of clearing storage by hand. Fall back to the staff token instead, which is
  // exactly the state "Switch sidekick" leaves behind.
  const activeToken = localStorage.getItem('access_token');
  const staffToken = localStorage.getItem('staff_access_token');

  // Impersonation tokens are minted after the staff token with the same lifetime,
  // so the staff token has usually expired by now. Don't offer a picker it can't
  // load. A token with no readable expiry stays untested and gets the fallback.
  if (staffToken && staffToken !== activeToken && !isKnownExpired(staffToken)) {
    // The impersonation token died; the staff token may still be good. Drop back
    // to the picker rather than the login screen.
    localStorage.setItem('access_token', staffToken);
    localStorage.removeItem('user_data');
    queryClient.setQueryData(["user"], null);
    toast({
      title: "Impersonation session ended",
      description: "Pick a sidekick again to continue.",
      variant: "destructive",
    });
    return;
  }

  // Either there is no staff session, or the staff token IS the one that just
  // 401'd — re-writing it would loop. Clear everything and go to /auth.
  expireSession();
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  
  // Get the JWT token from localStorage if it exists
  const token = localStorage.getItem('access_token');
  
  const headers: Record<string, string> = {};
  
  // Add Content-Type if we have data
  if (data) {
    headers["Content-Type"] = "application/json";
  }
  
  // Add Authorization header if we have a token
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  if (res.status === 401) {
    handleUnauthorized();
    throw new Error("401: Unauthorized");
  }

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const path = queryKey[0] as string;
    const fullUrl = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
    
    // Get the JWT token from localStorage if it exists
    const token = localStorage.getItem('access_token');
    
    // Prepare headers
    const headers: Record<string, string> = {};
    
    // Add Authorization header if we have a token
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    const res = await fetch(fullUrl, {
      headers,
      credentials: "include",
    });

    if (res.status === 401) {
      if (unauthorizedBehavior === "returnNull") {
        return null;
      }
      handleUnauthorized();
      throw new Error("401: Unauthorized");
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      // Bounded (was Infinity) so data un-invalidated by any mutation still
      // self-corrects on remount/navigation. With focus-refetch and interval
      // refetch off, this can only trigger fetches on mount — no storms.
      staleTime: 5 * 60_000,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
